import i18nFactory from '@yandex-int/i18n';
import { Image } from 'lego-on-react';
import * as React from 'react';
import { RefObject } from 'react';

import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { Shape2, Shape2Icon, Shape2Tone } from '../../components/Shape2/Shape2';
import { Bem } from '../../service/Bem/Bem';
import { CanvasEditableLabelContainer } from '../CanvasEditableLabel/CanvasEditableLabelContainer';

import './GalleryItem.css';
import * as keyset from './GalleryItem.i18n';

const i18n = i18nFactory(keyset);

export type HoverData = {
    index: number;
    ref: RefObject<HTMLDivElement>;
};

interface GalleryItemProps {
    thumbnailUrl: string;
    removable: boolean;
    resourceId: string;
    collectionIndex: number;
    name?: string;

    hoverItem?: (hoverData?: HoverData) => void;
    removeItem: (collectionIndex: number, itemName?: string) => void;
    editItemName: (collectionIndex: number, name: string) => void;
    previewItem: (collectionIndex?: number) => void;
    applyItem: (collectionIndex?: number) => void;
}

const BLOCK = 'gallery-item-dna';

export class GalleryItem extends React.PureComponent<GalleryItemProps> {
    private ref: RefObject<HTMLDivElement> = React.createRef();

    render() {
        const showName = this.props.name !== undefined;

        return (
            <div
                ref={this.ref}
                className={Bem.cls({
                    block: BLOCK,
                    mods: {
                        'show-name': showName ? 'yes' : ''
                    }
                })}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}
            >
                {this.renderItemContent()}
                {showName && this.renderItemName()}
            </div>
        );
    }

    private onMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        const { collectionIndex, hoverItem } = this.props;

        if (hoverItem) {
            hoverItem({
                index: collectionIndex,
                ref: this.ref
            });
        }
    };

    private onMouseLeave = () => {
        const { hoverItem } = this.props;

        if (hoverItem) {
            hoverItem();
        }
    };

    private renderItemContent() {
        return (
            <div className={Bem.cls(BLOCK, 'item-content')}>
                <div className={Bem.cls(BLOCK, 'thumb')}>
                    <Image cls={Bem.cls(BLOCK, 'image')} url={this.props.thumbnailUrl} />
                </div>
                {this.renderItemActions()}
            </div>
        );
    }

    private renderItemActions() {
        return (
            <div className={Bem.cls(BLOCK, 'actions')}>
                <div className={Bem.cls(BLOCK, 'action')}>
                    <ButtonDecorator
                        cls={Bem.cls(BLOCK, 'apply')}
                        theme={'action'}
                        size={'s'}
                        onClick={this.onApplyActionClick}
                    >
                        <Shape2 cls={Bem.cls(BLOCK, 'btn-shape')} width={16} height={16} icon={Shape2Icon.plus} />
                        {i18n('select')}
                    </ButtonDecorator>
                </div>
                <div className={Bem.cls(BLOCK, 'action')}>
                    <ButtonDecorator
                        cls={Bem.cls(BLOCK, 'view')}
                        theme={'normal'}
                        size={'s'}
                        onClick={this.onViewActionClick}
                    >
                        <Shape2 cls={Bem.cls(BLOCK, 'btn-shape')} width={16} height={16} icon={Shape2Icon.tick} />
                        {i18n('zoom-in-button-text')}
                    </ButtonDecorator>
                </div>
                {this.props.removable && (
                    <Shape2
                        cls={Bem.cls(BLOCK, 'remove-icon')}
                        width={16}
                        height={16}
                        tone={Shape2Tone.white}
                        icon={Shape2Icon.cross}
                        onClick={this.removeItem}
                    />
                )}
            </div>
        );
    }

    private onApplyActionClick = () => {
        this.props.applyItem(this.props.collectionIndex);
    };

    private onViewActionClick = () => {
        this.props.previewItem(this.props.collectionIndex);
    };

    private removeItem = () => {
        const { name, collectionIndex } = this.props;

        this.props.removeItem(collectionIndex, name);
    };

    private renderItemName() {
        const { name = '' } = this.props;
        return (
            <div className={Bem.cls(BLOCK, 'name')}>
                <CanvasEditableLabelContainer
                    editorId={this.props.resourceId}
                    savedValue={name}
                    onValueSaved={this.editItemName}
                    editBtnCls={Bem.cls(BLOCK, 'edit-name-btn')}
                    editInputCls={Bem.cls(BLOCK, 'edit-name-input')}
                    cls={Bem.cls(BLOCK, 'label')}
                />
            </div>
        );
    }

    private editItemName = (savedValue: string) => {
        this.props.editItemName(this.props.collectionIndex, savedValue);
    };
}
